type IsoDate = string;
type uuid = string;

export enum PermissionType {
  CREATE = 'CREATE',
  READ = 'READ',
  MODIFY = 'MODIFY',
  DELETE = 'DELETE',
}

export interface Permission {
  id: number;
  name: string;
  description: string;
  type: PermissionType;
}

export interface UserDetails {
  sessionId: uuid;
  userId: string;
  loginTime: IsoDate;
  lastRequestTime: IsoDate;
  requestCount: number;
  status: number;
  permissions: Permission[];
}
export interface SingletonInterface {
  // setters
  setHostName(hostName: string): void;
  setPath(path: string): void;
  setDil(DIL: DILList): void;
  setUserDetails(userDetails: UserDetails): void;
  // getters
  getHostName(): string | null;
  getPath(): string | null;
  getDIL(): DILList | null;
}

export interface Result {
  resultId: number;
  count: number;
  queryId: number;
  created: IsoDate;
  parentId?: number;
  sources?: {
    systemDefinitionId: number;
    dilDefinitionId: number;
    documentCount: number;
  };
}

export interface DILProperty {
  systemDefinitionId: number;
  propertyId: number;
  name: string;
  type: PropertyType;
  flags: PropertyFlag[];
}

export interface DILPropertyList {
  [key: string]: DILProperty;
}

export interface DIL {
  name: string;
  systemDefinitionId: number;
  dilId: uuid;
  dilDefinitionId: number;
  count: number;
  propertyList: DILPropertyList;
}

export interface SRSValuesResult<PropertyValues> {
  documentId: number;
  externalId: string;
  systemDefinitionId?: number;
  propertyValues?: PropertyValues;
}

export interface DILList {
  [key: string]: DIL;
}

/**
 * String values to represent the PropertyType enum from SRS
 */
export enum PropertyType {
  /**  An unspecified type: currently unused */
  Unspecified = 'Unspecified',
  /** Represents a tuple of a variable number of object components */
  Tuple = 'Tuple',
  /** Represents a document as a whole */
  Document = 'Document',
  /** Represents a block of structured text that is valid only in its entirety; 'New York Times' */
  String = 'String',
  /** Represents a block of unstructured text; for example, the body of text from a news story */
  Text = 'Text',
  /** Represents a date value */
  Temporal = 'Temporal',
  /** Represents a numeric value */
  Numeric = 'Numeric',
  /** Represents a latitude, longitude coordinate; '34.5,-127.39'. Either comma or space delimited */
  GeoPoint = 'GeoPoint',
  /** Represents an array of latitude, longitude coordinate; '34.5,-127.39'. Either comma or space delimited */
  GeoLine = 'GeoLine',
  /** Represents a value that indicates two states; true/false */
  Boolean = 'Boolean',
}

export interface HistogramEndpointMinorBin {
  label: string;
  start: IsoDate;
  end: IsoDate;
  count: number;
}

export interface HistogramEndpointMajorBin {
  binData: HistogramEndpointMinorBin[];
  start: IsoDate;
  end: IsoDate;
  startLabel: string;
  endLabel: string;
}

export interface HistogramEndpointResponse {
  bins: HistogramEndpointMajorBin[];
  startLabel: string;
  endLabel: string;
  startTime: IsoDate;
  endTime: IsoDate;
  majorBinResolution: string;
  minorBinResolution: string;
}

///this will be in middleware. Make sure to delete this and replace with updated middleware import before merging this PR
export interface GeoJsonParams {
  metadataPropertyNames?: string[];
  includeDocumentIds?: boolean;
  enableCaching?: boolean;
}

/**
 * An enumeration of all potential flags describing the valid uses for a particular document property
 */
export enum PropertyFlag {
  /** No flags */
  None = 'None',
  /** This property can be used by a DIL to configure queries  */
  Queryable = 'Queryable',
  /** This property exists in a document instance */
  Document = 'Document',
  /** This property is a user-specific property where the values are not captured in the document */
  UserSpecific = 'UserSpecific',
  /** This property is suitable as a label for a document instance */
  Label = 'Label',
  /** This property in general should not be made visible to end-users */
  Hidden = 'Hidden',
  /** This property (typically for a URI) can be used as an external reference to display a documents contents, rather than the actual document XML*/
  Navigable = 'Navigable',
  /** This property is categorical in nature and should be used in situations where discrete categorical values are preferred */
  Categorical = 'Categorical',
  /** This property is a delimited string representing hierarchical value  */
  Hierarchical = 'Hierarchical',
  /** This property is recommended to be included in generic text searches */
  Searchable = 'Searchable',
  /** This property is a multivalued property and may contain zero or more value instances */
  Multivalued = 'Multivalued',
  /** This property is a sub property of a tuple property */
  SubProperty = 'SubProperty',
  /** This property was created by the system based on enhancement operations performed on the data */
  Derived = 'Derived',
}

/**
 * An object with key names for parameters used for SRS calls.
 * NOTE!: SrsParameters are specific to endpoints. Not all of these parameters can be used for any endpoint.
 * For specifics on which parameters can be used, click the "API Documentation" link on https://hagar.pnl.gov/SRS/dev/status/links for the SRS deployment you are working with.
 */
export type SrsParameters = {
  maxClusters?: number;
  metadata?: any;
  maxNodes?: number;
  MaxValues?: number;
  sortProperty?: any;
  maxRoot?: any;
  aggregationProperty?: any;
  descending?: boolean;
  startIndex?: number;
  endIndex?: number;

  //catch-all for any we haven't added yet
  [name: string]: any;
};
